import { getClientCredentialsToken } from './auth/client-credentials';
import type { ApiErrorResponse } from './types/ApiErrorResponse.types';
import type { ApiResponse } from './types/ApiResponse.types';
import { getSsrUserAgentHeader } from './user-agent';

// CIN-14084: re-exported so apps register their identity from the same module they import setAuth
// from — controls the per-client outbound SSR User-Agent used for WAF first-party identification.
export { setApiClient } from './user-agent';

// CIN-11360: describe the Next.js fetch Data-Cache option locally so @cineamo/api does not depend on
// Next's global RequestInit augmentation. The `next` field is read by Next at runtime and ignored
// elsewhere — declaring it locally keeps the package framework-agnostic at the type level.
type NextFetchRequestConfig = { revalidate?: number | false; tags?: string[] };
type CineamoRequestInit = RequestInit & { next?: NextFetchRequestConfig };

export const DEFAULT_CACHING: CineamoRequestInit = {
    next: {
        revalidate: 60 * 30 // every 30 minutes
    }
};

type FetchClientResponse<T> = { isError?: undefined | boolean; status: number; headers: Headers; data: T };

// The payload is unconstrained rather than `ApiResponse | ApiErrorResponse`: not every route
// answers with a HAL document — the ticket/invoice routes negotiate application/pdf, .pkpass or a
// Google Wallet JWT through the Accept header and hand back the raw payload (see
// toFetchClientResponse). The default keeps every bare call site inferring what it did before.

type FetchClient = {
    get: <T = ApiResponse | ApiErrorResponse>(route: string, config?: RequestConfig) => Promise<FetchClientResponse<T>>;
    post: <T = ApiResponse | ApiErrorResponse>(
        route: string,
        data?: object,
        config?: RequestConfig
    ) => Promise<FetchClientResponse<T>>;
    put: <T = ApiResponse | ApiErrorResponse>(
        route: string,
        data?: object,
        config?: RequestConfig
    ) => Promise<FetchClientResponse<T>>;
    patch: <T = ApiResponse | ApiErrorResponse>(
        route: string,
        data?: object,
        config?: RequestConfig
    ) => Promise<FetchClientResponse<T>>;
    delete: <T = ApiResponse | ApiErrorResponse>(
        route: string,
        data?: object,
        config?: RequestConfig
    ) => Promise<FetchClientResponse<T>>;
};

type FetchClientConfig = {
    baseURL?: string;
};

export type RequestConfig = {
    revalidate?: false | number;
    headers?: Record<string, string>;
    session?: CineamoSession;
    credentials?: RequestInit['credentials'];
    // Request timeout in milliseconds; aborts the underlying fetch so slow uploads/requests
    // can't hang forever. Defaults to DEFAULT_REQUEST_TIMEOUT_MS. Pass 0 (or a negative value)
    // to disable the timeout for a specific call.
    timeout?: number;
};

// CIN-14192: per-request timeout is OPT-IN (0 = disabled by default). A blanket default timeout
// silently aborted legitimate long requests (large image/CSV uploads over slow uplinks), which the
// swallow-to-null upload fetchers turned into invisible failures. Callers that want a bound pass
// `timeout` explicitly.
export const DEFAULT_REQUEST_TIMEOUT_MS = 0;

/**
 * Build an AbortSignal that fires after the configured timeout. Returns a no-op signal when the
 * timeout is disabled (<= 0) or AbortController is unavailable. Always call `clear()` once the
 * request settles to release the timer.
 */
function createTimeout(config?: RequestConfig): { signal?: AbortSignal; clear: () => void } {
    const timeout = config?.timeout ?? DEFAULT_REQUEST_TIMEOUT_MS;
    if (timeout <= 0 || typeof AbortController === 'undefined') {
        return { signal: undefined, clear: () => {} };
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    return { signal: controller.signal, clear: () => clearTimeout(timer) };
}

// CIN-11360: @cineamo/api is framework-agnostic. Instead of importing next-auth here, each app
// injects its session getters — server-side `auth()` via setAuth(), client-side `getSession()`
// (from next-auth/react) via setClientAuth(). We only read `accessToken` off the session, so a
// minimal structural type is enough and keeps next-auth out of this package.
type InjectedSession = { accessToken?: string } | null;
type AuthFunction = () => Promise<InjectedSession>;

let authFn: AuthFunction | null = null;
let clientAuthFn: AuthFunction | null = null;

/** Inject the server-side session getter (next-auth `auth()`). Set once per app, server-side. */
export function setAuth(fn: AuthFunction) {
    authFn = fn;
}

/** Inject the client-side session getter (next-auth `getSession` from `next-auth/react`). */
export function setClientAuth(fn: AuthFunction) {
    clientAuthFn = fn;
}

type CineamoSession = {
    scheme: 'Bearer' | 'CineamoCart' | 'MovieDateToken';
    accessToken: string;
};

const getMovieDateTokenFromUrl = (): string | null => {
    if (typeof window === 'undefined') return null;

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    // Validate token is exactly 16 characters
    if (token && token.length === 16) {
        return token;
    }

    return null;
};

const getAuthHeaders = (session: CineamoSession): Record<string, string> => {
    if (session.scheme === 'MovieDateToken') {
        return { 'X-Cineamo-Movie-Date-Auth': `Token ${session.accessToken}` };
    } else {
        return { Authorization: `${session.scheme} ${session.accessToken}` };
    }
};

// Client-side session cache to avoid redundant /api/auth/session requests
let cachedSession: { session: InjectedSession; timestamp: number } | null = null;
let inFlightSessionPromise: Promise<InjectedSession> | null = null;
const SESSION_CACHE_TTL = 30000; // 30 seconds

const getSession = async (
    config?: RequestConfig,
    options?: { machineTokenFallback?: boolean }
): Promise<CineamoSession | null> => {
    // Prioritize explicit session config over automatic session detection
    if (config?.session) {
        return config.session;
    }

    let session: InjectedSession = null;
    try {
        if (process.env.NEXT_PUBLIC_NEXT_AUTH_ENABLED !== 'false') {
            // Server-side: use the injected auth() if available
            // Client-side: use the injected getSession() with deduplication + cache
            if (authFn) {
                // We're on the server (authFn is only set server-side)
                session = await authFn();
            } else if (typeof window !== 'undefined' && clientAuthFn) {
                // Check client-side cache first
                if (cachedSession && Date.now() - cachedSession.timestamp < SESSION_CACHE_TTL) {
                    session = cachedSession.session;
                } else {
                    // Deduplicate concurrent requests: reuse in-flight promise
                    if (!inFlightSessionPromise) {
                        inFlightSessionPromise = clientAuthFn().finally(() => {
                            inFlightSessionPromise = null;
                        });
                    }
                    session = await inFlightSessionPromise;
                    cachedSession = { session, timestamp: Date.now() };
                }
            }
        }
    } catch {
        // ignore
    }

    if (session?.accessToken) {
        return { scheme: 'Bearer', accessToken: session.accessToken };
    }

    // CIN-14171: no user session on the server ⇒ identify the SSR itself via a cached OAuth
    // client_credentials token. The API accepts it (ClientCredentialsGuardMiddleware) and the
    // WAF's Bot Control scope-down exempts Authorization-carrying requests, so our own SSR stops
    // looking like an anonymous bot. Fails open: null ⇒ the request goes out anonymous as before.
    // CIN-14326: NEVER on GET — a machine token on an anonymous read costs CDN cacheability for
    // nothing. Two mechanisms, both keyed on the mere presence of an Authorization header:
    //   1. Every API cache policy keys on `X-Cineamo-Authenticated`, which the CloudFront viewer
    //      function sets for any bearer token (CachePolicyV2 keys on the whole header). A machine
    //      token therefore moves SSR reads into a cache partition separate from the anonymous
    //      visitor traffic that requests the same URLs — same bytes, stored twice, both halves
    //      with a worse hit rate.
    //   2. TenantCacheabilityMiddleware marks any tenant-scoped response `Cache-Control: private`
    //      when an Authorization header is present, platform callers included — so on those routes
    //      the response is not cached at all.
    // The token also buys no WAF exemption here: Bot Control is scoped down on the
    // `cineamo:first-party` label from the SSR User-Agent, not on the header.
    // This comment previously said CloudFront refuses to cache ANY response to a request carrying
    // an Authorization header. That is false — measured on GET /contents/{contentId} with a user
    // token: miss, then `X-Cache: Hit from cloudfront`. Recorded rather than deleted because it
    // reads plausibly and would otherwise be reasoned out again.
    // Anonymous reads identify via the SSR User-Agent only; writes keep the token (never cacheable).
    if (options?.machineTokenFallback !== false && typeof window === 'undefined') {
        const machineToken = await getClientCredentialsToken();
        if (machineToken) {
            return { scheme: 'Bearer', accessToken: machineToken };
        }
    }

    // Check for movie date token if user is not authenticated (client-side only)
    const movieDateToken = getMovieDateTokenFromUrl();
    if (movieDateToken) {
        return { scheme: 'MovieDateToken', accessToken: movieDateToken };
    }

    return null;
};

const apiClient = ({ baseURL }: FetchClientConfig): FetchClient => ({
    get: async <T = ApiResponse | ApiErrorResponse>(
        route: string,
        config?: RequestConfig
    ): Promise<FetchClientResponse<T>> => {
        const session = await getSession(config, { machineTokenFallback: false });
        const { signal, clear } = createTimeout(config);
        try {
            const res = await fetch(constructRoute(baseURL, route), {
                credentials: config?.credentials ? config.credentials : 'include',
                method: 'GET',
                // CIN-14561: no Content-Type on a GET. It has no body to describe, and it is not a
                // CORS-safelisted request header — sending it makes the browser preflight every
                // single read. Preflights are cached per URL, so with one URL per movie this was
                // 38% of all api.cineamo.com requests.
                headers: {
                    ...getSsrUserAgentHeader(),
                    ...(session ? getAuthHeaders(session) : {}),
                    ...config?.headers
                },
                next: {
                    // Deliberately 0: API responses are cached at CloudFront (per-path TTLs on the
                    // api distribution), not in the Next Data Cache — the `revalidate` option is
                    // accepted for compatibility but never forwarded (CIN-14561 review).
                    revalidate: 0
                },
                signal
            } as CineamoRequestInit);

            return await toFetchClientResponse(res);
        } finally {
            clear();
        }
    },
    post: async <T = ApiResponse | ApiErrorResponse>(
        route: string,
        data = {},
        config?: RequestConfig
    ): Promise<FetchClientResponse<T>> => {
        const session = await getSession(config);
        const isFormData = data instanceof FormData;
        const isFile = data instanceof File;
        const { signal, clear } = createTimeout(config);
        try {
            const res = await fetch(constructRoute(baseURL, route), {
                credentials: 'include',
                method: 'POST',
                headers: {
                    // Don't set Content-Type for FormData - browser sets it with boundary
                    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
                    ...getSsrUserAgentHeader(),
                    ...(session ? getAuthHeaders(session) : {}),
                    ...config?.headers
                },
                body: isFormData || isFile ? data : JSON.stringify(data),
                signal
            });

            return await toFetchClientResponse(res);
        } finally {
            clear();
        }
    },
    put: async <T = ApiResponse | ApiErrorResponse>(
        route: string,
        data = {},
        config?: RequestConfig
    ): Promise<FetchClientResponse<T>> => {
        const session = await getSession(config);
        const authHeaders = session ? getAuthHeaders(session) : {};
        const normalizedHeaders = getNormalizedHeaders({
            ...getSsrUserAgentHeader(),
            ...authHeaders,
            ...(config?.headers ?? {})
        });
        if (!normalizedHeaders['content-type']) {
            normalizedHeaders['content-type'] = 'application/json';
        }
        const { signal, clear } = createTimeout(config);
        try {
            const res = await fetch(constructRoute(baseURL, route), {
                credentials: 'include',
                method: 'PUT',
                headers: normalizedHeaders,
                body: data instanceof File ? data : JSON.stringify(data),
                signal
            });

            return await toFetchClientResponse(res);
        } finally {
            clear();
        }
    },
    patch: async <T = ApiResponse | ApiErrorResponse>(
        route: string,
        data = {},
        config?: RequestConfig
    ): Promise<FetchClientResponse<T>> => {
        const session = await getSession(config);
        const { signal, clear } = createTimeout(config);
        try {
            const res = await fetch(constructRoute(baseURL, route), {
                credentials: 'include',
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    ...getSsrUserAgentHeader(),
                    ...(session ? getAuthHeaders(session) : {}),
                    ...config?.headers
                },
                body: data instanceof File ? data : JSON.stringify(data),
                signal
            });

            return await toFetchClientResponse(res);
        } finally {
            clear();
        }
    },
    delete: async <T = ApiResponse | ApiErrorResponse>(
        route: string,
        data = {},
        config?: RequestConfig
    ): Promise<FetchClientResponse<T>> => {
        const session = await getSession(config);
        const { signal, clear } = createTimeout(config);
        try {
            const res = await fetch(constructRoute(baseURL, route), {
                credentials: 'include',
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    ...getSsrUserAgentHeader(),
                    ...(session ? getAuthHeaders(session) : {}),
                    ...config?.headers
                },
                body: data instanceof File ? data : JSON.stringify(data),
                signal
            });

            return await toFetchClientResponse(res);
        } finally {
            clear();
        }
    }
});

/**
 * This will normalize the header 'keys' to remove / prevent from duplicates like 'Content-Type' and 'content-type'.
 * E.g. 'Content-Type' will become 'content-type'
 * @param headers
 */
function getNormalizedHeaders(headers: Record<string, string>): Record<string, string> {
    const normalizedHeaders: Record<string, string> = {};
    Object.keys(headers).forEach((key) => {
        normalizedHeaders[key.toLowerCase()] = headers[key];
    });
    return normalizedHeaders;
}

async function toFetchClientResponse<T = ApiResponse | ApiErrorResponse>(
    res: Response
): Promise<FetchClientResponse<T>> {
    let body = null;
    const contentType = res.headers?.get('content-type')?.toLowerCase() || 'application/json';
    try {
        if (contentType.startsWith('text/') || contentType === 'application/vnd.google.wallet.jwt') {
            body = await res.text();
        } else if (
            contentType === 'application/pdf' ||
            contentType === 'application/xml' ||
            contentType.startsWith('application/xml') ||
            contentType === 'application/vnd.apple.pkpass' ||
            contentType === 'application/vnd.apple.pkpasses'
        ) {
            body = await res.blob();
        } else {
            body = await res.json();
        }
    } catch {
        // ignore
    }

    if (!res.ok) {
        // this rejects the Promise, so the Promise.error() handler is called instead of .then()
        throw {
            isError: true,
            status: res.status,
            headers: res.headers,
            data: {
                isError: true, // TODO: is it still needed?
                ...body
            }
        };
    }

    return {
        status: res.status,
        headers: res.headers,
        data: body
    };
}

export function constructRoute(base?: string, route?: string): string | URL {
    if (!route) return '';

    const isAbsolute = /^https?:\/\//i.test(route);

    let url: URL | null = null;

    if (isAbsolute) {
        url = new URL(route);
    } else if (base) {
        const cleanBase = base.endsWith('/') ? base : base + '/';
        const cleanRoute = route.replace(/^\/+/, '');
        url = new URL(cleanRoute, cleanBase);
    }

    if (!url) return route;

    if (process.env.NEXT_PUBLIC_DISABLE_API_CACHE === 'true') {
        url.searchParams.set('_t', Date.now().toString());
    }

    return url;
}

export function createFetchClient(config: { baseURL?: string }) {
    return apiClient(config);
}
